import {createServerFn} from '@tanstack/react-start';
import {getRequest} from '@tanstack/react-start/server';

interface IpApiResponse {
  status: string;
  message?: string;
  lat?: number;
  lon?: number;
  city?: string;
  country?: string;
  countryCode?: string;
  region?: string;
  regionName?: string;
  query?: string;
}

/** Returns true if the string is a valid dotted-decimal IPv4 address. */
function isIPv4(ip: string): boolean {
  const parts = ip.split('.');
  if (parts.length !== 4) return false;
  return parts.every((p) => /^\d+$/.test(p) && Number(p) <= 255);
}

/** Returns true if the string is a valid IPv6 address. */
function isIPv6(ip: string): boolean {
  return /^([\da-f]{0,4}:){2,7}[\da-f]{0,4}$/i.test(ip);
}

/**
 * Strips port suffix from an IP string.
 * Handles IPv4 (1.2.3.4:port) and bracketed IPv6 ([::1]:port).
 */
function stripPort(raw: string): string {
  if (raw.startsWith('[')) {
    const end = raw.indexOf(']');
    return end !== -1 ? raw.slice(1, end) : raw;
  }
  // IPv4 with port has exactly one colon
  const colons = (raw.match(/:/g) ?? []).length;
  if (colons === 1) {
    return raw.split(':')[0] ?? raw;
  }
  return raw;
}

/**
 * Returns true when the IP is private, loopback, or otherwise non-routable
 * so we can skip the external geo lookup.
 * Covers common IPv4 and IPv6 reserved ranges.
 */
function isPrivateOrReservedIp(ip: string): boolean {
  // Loopback + unresolved placeholder
  if (ip === '127.0.0.1' || ip === '::1' || ip === 'localhost') {
    return true;
  }

  // Private IPv4 ranges
  const ipv4Private = [
    /^10\./, // 10.0.0.0/8
    /^172\.(1[6-9]|2\d|3[01])\./, // 172.16.0.0/12
    /^192\.168\./, // 192.168.0.0/16
    /^169\.254\./, // Link-local 169.254.0.0/16
    /^0\./, // Current network 0.0.0.0/8
    /^100\.(6[4-9]|[7-9]\d|1[01]\d|12[0-7])\./, // Shared address space 100.64.0.0/10
  ];

  if (ipv4Private.some((r) => r.test(ip))) return true;

  // Private / reserved IPv6 ranges
  const lower = ip.toLowerCase();
  if (lower === '::' || lower === '::1') return true;
  if (/^fe[89ab][0-9a-f]:/i.test(lower)) return true; // Link-local fe80::/10
  if (/^f[cd]/i.test(lower)) return true; // Unique-local fc00::/7
  if (lower.startsWith('2001:db8:')) return true; // Documentation 2001:db8::/32

  return false;
}

interface LocationResult {
  lat: number;
  lng: number;
  city?: string;
  country?: string;
  countryCode?: string;
  region?: string;
  regionName?: string;
  ip?: string;
}

const defaultUserdata: LocationResult = {
  lat: 0,
  lng: 0,
};

export interface ResolvedLocation {
  lat: number | null;
  lng: number | null;
  region: string;
}

/**
 * Resolves approximate geolocation from the IP address in a Request object.
 * Handles private/local IPs gracefully — returns null coords and 'unknown' region.
 * API errors are logged but never propagate; signup flows should never fail due to geolocation.
 */
export async function resolveLocationFromRequest(
  request: Request
): Promise<ResolvedLocation> {
  try {
    const forwardedFor = request.headers.get('x-forwarded-for');
    const realIp = request.headers.get('x-real-ip');
    const rawIp = forwardedFor?.split(',')[0]?.trim() || realIp || '127.0.0.1';

    // Strip port suffix then validate format to prevent header-injection into the URL.
    const clientIp = stripPort(rawIp);
    if (!isIPv4(clientIp) && !isIPv6(clientIp)) {
      return {lat: null, lng: null, region: 'unknown'};
    }

    if (isPrivateOrReservedIp(clientIp)) {
      return {lat: null, lng: null, region: 'unknown'};
    }

    const fields =
      'status,message,lat,lon,city,region,regionName,country,countryCode,query';
    // NOTE: ip-api.com free tier only supports HTTP. To use HTTPS, a paid plan
    // is required (https://ip-api.com/#pro). Upgrade before sending to production
    // if the Lambda egress is not otherwise secured at the network level.
    const apiUrl = `http://ip-api.com/json/${clientIp}?fields=${fields}`;

    const response = await fetch(apiUrl, {
      signal: AbortSignal.timeout(3000),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = (await response.json()) as IpApiResponse;

    if (data.status !== 'success') {
      if (data.message === 'reserved range') {
        return {lat: null, lng: null, region: 'unknown'};
      }
      throw new Error(data.message || 'Failed to get location data');
    }

    if (typeof data.lat !== 'number' || typeof data.lon !== 'number') {
      throw new Error('Invalid location data received');
    }

    const region =
      data.city && data.country
        ? `${data.city}, ${data.country}`
        : 'unknown';

    return {lat: data.lat, lng: data.lon, region};
  } catch (error) {
    console.error('Error resolving location from request:', error);
    return {lat: null, lng: null, region: 'unknown'};
  }
}

/**
 * Gets the requesters approximate location via IP Geolocation.
 *
 * @returns the lat/lng of the requester's IP from ip-api
 */
export const getUserLocationFromIp = createServerFn({
  method: 'GET',
}).handler(async () => {
  const request = getRequest();

  if (!request) {
    return defaultUserdata;
  }

  try {
    // Get client IP from request headers
    const forwardedFor = request.headers.get('x-forwarded-for');
    const realIp = request.headers.get('x-real-ip');
    const clientIp =
      forwardedFor?.split(',')[0]?.trim() || realIp || '127.0.0.1';

    // Skip API call for private/reserved IPs (local development)
    if (isPrivateOrReservedIp(clientIp)) {
      return defaultUserdata;
    }

    // Build API URL with specific fields to minimize bandwidth
    const fields =
      'status,message,lat,lon,city,region,regionName,country,countryCode,query';
    const apiUrl = `http://ip-api.com/json/${clientIp}?fields=${fields}`;

    // Make request to ip-api service
    const response = await fetch(apiUrl);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = (await response.json()) as IpApiResponse;

    // Check if the API request was successful
    if (data.status !== 'success') {
      // Don't log error for reserved range - this is expected in some environments
      if (data.message === 'reserved range') {
        return defaultUserdata;
      }
      throw new Error(data.message || 'Failed to get location data');
    }

    // Ensure we have required lat/lon data
    if (typeof data.lat !== 'number' || typeof data.lon !== 'number') {
      throw new Error('Invalid location data received');
    }

    const result: LocationResult = {
      lat: data.lat,
      lng: data.lon,
      city: data.city,
      country: data.country,
      countryCode: data.countryCode,
      region: data.region,
      regionName: data.regionName,
      ip: data.query,
    };

    return result;
  } catch (error) {
    console.error('Error getting user location:', error);

    return defaultUserdata;
  }
});
